/**
 * useWFAQueue Hook
 * 
 * Manages Walk Forward Analysis queue state and operations.
 * Completely separate from optimization queue to prevent interference.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { notifyCreditChange } from './useCredits';
import wfaApiService, { type WFAJob, type WFAConfig } from '../services/wfa/WFAApiService';
import wfaEventService, { type WFAEvent } from '../services/wfa/WFAEventService';
import { jobsIntegrationService } from '../services/jobs/JobsIntegrationService';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unknown error occurred';
}

export interface UseWFAQueueOptions {
  autoConnect?: boolean;
  pollInterval?: number;
}

export interface WFAQueueState {
  jobs: WFAJob[];
  activeJobs: WFAJob[];
  completedJobs: WFAJob[];
  failedJobs: WFAJob[];
  loading: boolean;
  error: string | null;
  connected: boolean;
}

export const useWFAQueue = (options: UseWFAQueueOptions = {}) => {
  const { autoConnect = false, pollInterval = 10000 } = options;

  const [state, setState] = useState<WFAQueueState>({
    jobs: [],
    activeJobs: [],
    completedJobs: [],
    failedJobs: [],
    loading: false,
    error: null,
    connected: false
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch jobs from server
  const fetchJobs = useCallback(async () => {
    try {
      const response = await wfaApiService.getWFAJobs();
      
      if (response.success && response.jobs) {
        const jobs = response.jobs;
        const activeJobs = jobs.filter(job => job.status === 'running' || job.status === 'queued');
        const completedJobs = jobs.filter(job => job.status === 'completed');
        const failedJobs = jobs.filter(job => job.status === 'failed');
        
        setState(prev => ({
          ...prev,
          jobs,
          activeJobs,
          completedJobs,
          failedJobs,
          error: null
        }));
      } else {
        setState(prev => ({ ...prev, error: response.error || 'Failed to fetch jobs' }));
      }
    } catch (error: unknown) {
      setState(prev => ({ ...prev, error: getErrorMessage(error) }));
    }
  }, []);

  // Handle real-time WFA events
  const handleWFAEvent = useCallback((event: WFAEvent) => {
    setState(prev => {
      const jobs = [...prev.jobs];
      const jobIndex = jobs.findIndex(job => job.id === event.jobId);

      if (event.type === 'wfa-progress' && event.data && 'progress' in event.data) {
        if (jobIndex >= 0) {
          jobs[jobIndex] = {
            ...jobs[jobIndex],
            progress: event.data.progress,
            currentPeriod: event.data.currentPeriod,
            totalPeriods: event.data.totalPeriods,
            currentPhase: event.data.currentPhase,
            estimatedTimeRemaining: event.data.estimatedTimeRemaining
          };
        }
      } else if (event.type === 'wfa-completed') {
        if (jobIndex >= 0) {
          jobs[jobIndex] = {
            ...jobs[jobIndex],
            status: 'completed',
            progress: 100,
            completedAt: Date.now(),
            summary: event.data && 'summary' in event.data ? event.data.summary : undefined
          };

          notifyCreditChange();
          
          // Fetch full job details (including periodResults) before storing
          const jobId = jobs[jobIndex].id;
          wfaApiService.getWFAJob(jobId).then(response => {
            if (response.success && response.job) {
              wfaApiService.storeCompletedJob(response.job).catch(() => {
                // Silently handle storage errors
              });
            }
          }).catch(() => {
            // Silently handle fetch errors
          });
        } else if (event.jobId) {
          // Job not in local state (started from different tab/session)
          // Add it as completed
          const completedJob: WFAJob = {
            id: event.jobId,
            status: 'completed',
            progress: 100,
            currentPeriod: 0,
            totalPeriods: 0,
            currentPhase: 'Complete',
            startTime: Date.now(),
            completedAt: Date.now(),
            summary: event.data && 'summary' in event.data ? event.data.summary : undefined
          };
          jobs.push(completedJob);
        }
        
        // Fetch full job details after completion to update local state
        setTimeout(() => fetchJobs(), 500);
      } else if (event.type === 'wfa-failed') {
        if (jobIndex >= 0) {
          jobs[jobIndex] = {
            ...jobs[jobIndex],
            status: 'failed',
            completedAt: Date.now(),
            error: event.data && 'error' in event.data ? event.data.error : 'Unknown error'
          };
        }
      }

      const activeJobs = jobs.filter(job => job.status === 'running' || job.status === 'queued');
      const completedJobs = jobs.filter(job => job.status === 'completed');
      const failedJobs = jobs.filter(job => job.status === 'failed');

      return {
        ...prev,
        jobs,
        activeJobs,
        completedJobs,
        failedJobs
      };
    });
  }, [fetchJobs]);

  // Start WFA analysis
  const startWFA = useCallback(async (config: WFAConfig) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await wfaApiService.startWFA(config);

      if (response.success && response.jobId) {
        // Register with Jobs system for unified tracking
        jobsIntegrationService.registerOptimizationJob({
          jobId: response.jobId,
          symbol: config.symbol,
          interval: config.interval,
          strategy: config.strategy || 'Unknown',
          totalCombinations: config.totalPeriods || 0,
          jobType: 'WFA_RUN',
        });

        // Add new job to state immediately
        const newJob: WFAJob = {
          id: response.jobId,
          status: 'queued',
          config,
          progress: 0,
          currentPeriod: 0,
          totalPeriods: 0,
          currentPhase: 'Initializing...',
          startTime: Date.now()
        };

        setState(prev => ({
          ...prev,
          jobs: [...prev.jobs, newJob],
          activeJobs: [...prev.activeJobs, newJob],
          loading: false
        }));

        // Ensure SSE is connected
        if (!wfaEventService.isConnectedToSSE()) {
          wfaEventService.connect();
        }

        // Refresh jobs after a short delay
        setTimeout(() => fetchJobs(), 100);

        return response.jobId;
      } else {
        setState(prev => ({ ...prev, loading: false, error: response.error || 'Failed to start WFA' }));
        return null;
      }
    } catch (error: unknown) {
      setState(prev => ({ ...prev, loading: false, error: getErrorMessage(error) }));
      return null;
    }
  }, [fetchJobs]);

  // Remove WFA job
  const removeJob = useCallback(async (jobId: string) => {
    try {
      const response = await wfaApiService.removeWFAJob(jobId);
      
      if (response.success) {
        setState(prev => {
          const jobs = prev.jobs.filter(job => job.id !== jobId);
          return {
            ...prev,
            jobs,
            activeJobs: jobs.filter(job => job.status === 'running' || job.status === 'queued'),
            completedJobs: jobs.filter(job => job.status === 'completed'),
            failedJobs: jobs.filter(job => job.status === 'failed')
          };
        });
      }
    } catch (error: unknown) {
      setState(prev => ({ ...prev, error: getErrorMessage(error) }));
    }
  }, []);

  // Get specific job details
  const getJobDetails = useCallback(async (jobId: string) => {
    try {
      const response = await wfaApiService.getWFAJob(jobId);
      return response.success ? response.job : null;
    } catch (error: unknown) {
      return null;
    }
  }, []);

  // Connect to SSE
  const connect = useCallback(() => {
    try {
      wfaEventService.connect();
      setState(prev => ({ ...prev, connected: true }));
    } catch (error: unknown) {
      setState(prev => ({ ...prev, connected: false }));
    }
  }, []);

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    wfaEventService.disconnect();
    setState(prev => ({ ...prev, connected: false }));
  }, []);

  // Auto-connect and subscribe to events
  useEffect(() => {
    fetchJobs();

    // Subscribe to WFA events (but don't connect until needed)
    const unsubscribe = wfaEventService.subscribe(handleWFAEvent);

    // Only connect if autoConnect is true
    if (autoConnect) {
      connect();
    }

    return () => {
      unsubscribe();
      if (autoConnect) {
        disconnect();
      }
    };
  }, [autoConnect, connect, disconnect, fetchJobs, handleWFAEvent]);

  // Auto-connect when there are active jobs, disconnect when none
  useEffect(() => {
    if (state.activeJobs.length > 0 && !wfaEventService.isConnectedToSSE()) {
      connect();
    } else if (state.activeJobs.length === 0 && wfaEventService.isConnectedToSSE()) {
      // Note: We keep completed jobs in state even after disconnecting
      disconnect();
    }
  }, [state.activeJobs.length, connect, disconnect]);

  // Polling for active jobs
  useEffect(() => {
    if (state.activeJobs.length > 0) {
      pollIntervalRef.current = setInterval(() => {
        fetchJobs();
      }, pollInterval);
    } else {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [state.activeJobs.length, fetchJobs, pollInterval]);

  return {
    // State
    ...state,
    
    // Actions
    startWFA,
    removeJob,
    getJobDetails,
    fetchJobs,
    connect,
    disconnect
  };
};
